import * as Yup from "yup";

/**
 * The three report profiles the API builds (ResidentPdfReportService::TYPE_SECTIONS).
 *
 * `hospital_passport` is the emergency-transfer subset. `care_summary` adds
 * updates; `full_report` adds updates AND governance and is the widest resident
 * assembly in the product — it carries evaluations, care plans and the clinical
 * overview, which no reachable screen renders.
 *
 * Both of the latter two were dead code until 2026-07-27: the payload could not
 * express a type, so every request fell through to the service's
 * `hospital_passport` default.
 */
export const HOSPITAL_REPORT_TYPES = [
  "hospital_passport",
  "care_summary",
  "full_report",
];

/**
 * Menu copy for the three profiles, derived from the section sets above so the
 * picker can never offer a type the API does not build. Order follows
 * `HOSPITAL_REPORT_TYPES` (narrowest → widest); `hospital_passport` carries no
 * explicit type on the wire so it stays the API's own default.
 *
 * The copy must describe what the PDFs ACTUALLY contain. It previously promised
 * "48h / 7 days / 30 days of observations" as the difference between the three —
 * none of which is true: the observations section is commented out of all three
 * section sets (`observations_days` is dead) and every profile prints the same
 * 72h of recent observations. The real deltas are care_summary = + resident
 * updates, full_report = + updates AND governance actions.
 */
const HOSPITAL_REPORT_TYPE_COPY = {
  hospital_passport: {
    label: "Hospital passport",
    description:
      "Emergency transfer pack — identity, resuscitation, allergies, wounds, legal status. Top 5 care plans.",
  },
  care_summary: {
    label: "Care summary",
    description: "Passport plus all published care plans and resident updates.",
  },
  full_report: {
    label: "Full report",
    description:
      "Widest export — adds resident updates and governance actions. For records requests.",
  },
};

export const HOSPITAL_REPORT_TYPE_OPTIONS = HOSPITAL_REPORT_TYPES.map(
  (value) => ({ value, ...HOSPITAL_REPORT_TYPE_COPY[value] }),
);

/**
 * Payload for GET `/api/resident/{resident_id}/pdf-report`.
 */
export const exportHospitalReportPayloadSchema = Yup.object({
  resident_id: Yup.string().trim().uuid().required(),
  resident_name: Yup.string().trim().optional(),
  file_name: Yup.string().trim().optional(),
  // Omitted ⇒ the API's own default (hospital_passport), so existing callers
  // are byte-identical.
  type: Yup.string().trim().oneOf(HOSPITAL_REPORT_TYPES).optional(),
});

export const buildHospitalReportUrl = (residentId, type) =>
  `/api/resident/${residentId}/pdf-report${
    type && HOSPITAL_REPORT_TYPES.includes(type)
      ? `?type=${encodeURIComponent(type)}`
      : ""
  }`;

const slugifyFileName = (value) =>
  String(value)
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .toLowerCase();

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Local `YYYYMMDD-HHmm` stamp. The server names the file with its own generated
 * date, but `utils/api.js`'s response interceptor returns `response.data`, so a
 * blob download never sees `Content-Disposition` — stamping client-side is the
 * non-invasive way to stop today's export overwriting yesterday's in the
 * Downloads folder.
 */
const downloadStamp = (now = new Date()) =>
  `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}-` +
  `${pad2(now.getHours())}${pad2(now.getMinutes())}`;

export const buildHospitalReportDownloadName = ({
  resident_name,
  file_name,
  type,
} = {}) => {
  if (file_name) return file_name;
  const slug = resident_name ? slugifyFileName(resident_name) : "resident";
  // Every profile names itself — the default one used to fall back to a shared
  // `-hospital-report.pdf`, so exporting all three for one resident landed three
  // different documents on the same name. The stamp keeps repeat exports of the
  // SAME profile apart too.
  const profile = slugifyFileName(
    String(type || "hospital_passport").replace(/_/g, " "),
  );
  return `${slug}-${profile}-${downloadStamp()}.pdf`;
};
